// FieldIQ — Login, register, forgot-password, session-expired screens

import { useState, useEffect } from "react";
import { AUTH_URL, setToken } from "../lib/api";

// logoResolved defaults true so screens that never look a logo up (register,
// forgot password) show the FieldIQ mark immediately, as before.
const AuthCard = ({ children, facilitySlug, logoUrl, logoResolved = true }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 w-full max-w-sm">
      <div className="text-center mb-6">
        {logoUrl || logoResolved ? (
          <img
            src={logoUrl || "/logo-white.png"}
            alt="FieldIQ"
            className="h-12 mx-auto mb-3 object-contain"
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
        ) : (
          // Hold the space while the facility logo resolves. Showing the
          // FieldIQ mark here and swapping it out is what made the login
          // screen flash the wrong brand whenever the cached logo was absent.
          <div className="h-12 mb-3" aria-hidden="true" />
        )}
        <p className="text-sm text-slate-400">
          FieldIQ — Field Operations Platform
        </p>
        {facilitySlug && (
          <p className="text-xs text-cyan-400 mt-1 font-mono tracking-wide">
            {facilitySlug}
          </p>
        )}
      </div>
      {children}
    </div>
  </div>
);

const _authInput =
  "w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none";
const _authBtn =
  "w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50";

const ForgotPasswordScreen = ({ onBack, facilitySlug }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // facility_id ties the token to the tenant this login belongs to, so
        // the set-password page carries their branding and sends them back
        // here — not to whichever facility happens to sort first.
        body: JSON.stringify({
          email,
          app_url: window.location.origin,
          ...(facilitySlug ? { facility_id: facilitySlug } : {}),
        }),
      });
      const data = await res.json();
      if (res.ok) setSent(true);
      else setError(data.error || "Request failed");
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <AuthCard>
      {sent ? (
        <div className="text-center space-y-4">
          <p className="text-slate-300 text-sm">
            If that email is registered, you'll receive a password reset link
            shortly.
          </p>
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
          >
            ← Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-white font-semibold text-center">
            Reset Password
          </h2>
          <p className="text-slate-400 text-sm text-center">
            Enter your email and we'll send a reset link.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className={_authInput}
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button type="submit" disabled={loading} className={_authBtn}>
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthCard>
  );
};

const RegisterScreen = ({ onBack, facilitySlug }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    facility_id: facilitySlug || "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          facility_id: form.facility_id.trim().toUpperCase(),
        }),
      });
      const data = await res.json();
      if (res.ok) setSuccess(true);
      else setError(data.error || "Registration failed");
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <AuthCard>
      {success ? (
        <div className="text-center space-y-4">
          <p className="text-slate-300 text-sm">
            Account created! Check your email to confirm your address before
            signing in.
          </p>
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
          >
            ← Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <h2 className="text-white font-semibold text-center mb-1">
            Create Account
          </h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.first_name}
              onChange={set("first_name")}
              placeholder="First name"
              required
              className={_authInput}
            />
            <input
              type="text"
              value={form.last_name}
              onChange={set("last_name")}
              placeholder="Last name"
              required
              className={_authInput}
            />
          </div>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="Email"
            required
            className={_authInput}
          />
          {facilitySlug ? (
            <div className="px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-400 text-sm font-mono">
              {facilitySlug}
            </div>
          ) : (
            <input
              type="text"
              value={form.facility_id}
              onChange={set("facility_id")}
              placeholder="Facility ID (provided by your admin)"
              required
              className={_authInput}
            />
          )}
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            placeholder="Password (8+ characters)"
            required
            className={_authInput}
          />
          <input
            type="password"
            value={form.confirm_password}
            onChange={set("confirm_password")}
            placeholder="Confirm password"
            required
            className={_authInput}
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button type="submit" disabled={loading} className={_authBtn}>
            {loading ? "Creating account..." : "Create Account"}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthCard>
  );
};

export const LoginPage = ({ onLogin, facilitySlug, logoUrl: logoUrlProp }) => {
  const [screen, setScreen] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(logoUrlProp || null);
  // Nothing to wait for when the logo came from cache, or when the URL names
  // no facility to look one up for.
  const [logoResolved, setLogoResolved] = useState(
    !!logoUrlProp || !facilitySlug,
  );

  useEffect(() => {
    if (!facilitySlug) return;
    fetch(`${AUTH_URL}/widget-config/${encodeURIComponent(facilitySlug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.logo_url) setLogoUrl(data.logo_url);
      })
      .catch(() => {})
      .finally(() => setLogoResolved(true));
  }, [facilitySlug]);

  if (screen === "forgot")
    return (
      <ForgotPasswordScreen
        facilitySlug={facilitySlug}
        onBack={() => setScreen("login")}
      />
    );
  if (screen === "register")
    return (
      <RegisterScreen
        facilitySlug={facilitySlug}
        onBack={() => setScreen("login")}
      />
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          ...(facilitySlug ? { facility_id: facilitySlug } : {}),
        }),
      });
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        onLogin(data.user);
      } else {
        setError(data.error || "Login failed");
      }
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <AuthCard
      facilitySlug={facilitySlug}
      logoUrl={logoUrl}
      logoResolved={logoResolved}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
          className={_authInput}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          required
          className={_authInput}
        />
        {error && <p className="text-red-400 text-sm text-center">{error}</p>}
        <button type="submit" disabled={loading} className={_authBtn}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <div className="flex justify-between text-sm mt-2">
          <button
            type="button"
            onClick={() => setScreen("forgot")}
            className="text-slate-400 hover:text-cyan-400 transition-colors"
          >
            Forgot password?
          </button>
          <button
            type="button"
            onClick={() => setScreen("register")}
            className="text-slate-400 hover:text-cyan-400 transition-colors"
          >
            Create account
          </button>
        </div>
      </form>
    </AuthCard>
  );
};

// Shown over the app when a request hits 401. Re-login in place —
// app state (open modals, form drafts) is preserved and the failed
// request retries on success.
export const SessionExpiredModal = ({
  user,
  facilitySlug,
  onSuccess,
  onLogout,
}) => {
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          ...(facilitySlug ? { facility_id: facilitySlug } : {}),
        }),
      });
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        onSuccess(data.user);
      } else {
        setError(data.error || "Login failed");
      }
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
        <h2 className="text-white font-bold text-lg mb-1">Session expired</h2>
        <p className="text-slate-400 text-sm mb-4">
          Sign back in to continue — your unsaved work is still here.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className={_authInput}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            autoFocus
            className={_authInput}
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button type="submit" disabled={loading} className={_authBtn}>
            {loading ? "Signing in..." : "Sign In & Resume"}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="w-full py-2 text-slate-400 text-sm hover:text-white transition-colors"
          >
            Log out instead
          </button>
        </form>
      </div>
    </div>
  );
};
